import {
  Alert,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { CalendarOff, Pencil, Plus, Trash2 } from 'lucide-react';
import { type JSX, useState } from 'react';

import { useCurrentUser } from '../../../auth/use-current-user';
import {
  createHoliday,
  deleteHoliday,
  listHolidays,
  updateHoliday,
  ZONE_LABELS,
  ZONES,
  type Holiday,
} from '../../../calendar/api';
import { mapHolidayError } from '../../../calendar/calendar-api-errors';
import { formatCalendarDate } from '../../../calendar/format-calendar-date';
import {
  toHolidayCreateInput,
  toHolidayFormValues,
  toHolidayUpdateInput,
} from '../../../calendar/holiday-form-schema';
import { GuideLink } from '../../../guide/guide-link';
import { hasPermission } from '../../../auth/has-permission';
import { HolidayFormModal } from './components/holiday-form-modal';

// `protectedRoute` in `router.tsx` is pathless, so a child declared with
// `path: '/calendar/holidays'` still nests under it in the route id even
// though its URL is the absolute `/calendar/holidays`.
const holidaysRoute = getRouteApi('/protected/calendar/holidays');

type ModalState = { mode: 'create' } | { mode: 'edit'; holiday: Holiday } | null;

/**
 * `holidays/spec.md` → "Pantalla de gestión de feriados en back-office".
 * List state (`zone`/`year`/`page`) lives in the URL, same convention as
 * `CalendarListPage`. Read-only for a user without `menu.calendar.manage`
 * (UX mirror only — the API's 403 is the real authority).
 */
export function HolidayListPage(): JSX.Element {
  const search = holidaysRoute.useSearch();
  const navigate = holidaysRoute.useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();
  const [modal, setModal] = useState<ModalState>(null);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const canManage = hasPermission(currentUser.data, 'menu.calendar.manage');

  const holidaysQuery = useQuery({
    queryKey: ['holidays', search.zone, search.year, search.page],
    queryFn: () =>
      listHolidays({ zone: search.zone, year: search.year, page: search.page, pageSize: 100 }),
  });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['holidays'] });
  }

  const createMutation = useMutation({
    mutationFn: createHoliday,
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Feriado creado');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ReturnType<typeof toHolidayUpdateInput> }) =>
      updateHoliday(id, input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Feriado actualizado');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteHoliday,
    onMutate: () => setActionError(undefined),
    onSuccess: async () => {
      await invalidate();
      toast('success', 'Feriado eliminado');
    },
    onError: (error) => setActionError(mapHolidayError(error)),
  });

  function applyFilters(patch: Partial<typeof search>): void {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  const formError =
    modal?.mode === 'create'
      ? mapHolidayError(createMutation.error)
      : modal?.mode === 'edit'
        ? mapHolidayError(updateMutation.error)
        : undefined;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={CalendarOff}
        title="Feriados"
        description="Administrá los feriados por zona y su disponibilidad de servicio."
        actions={
          <div className="flex items-center gap-2">
            {canManage ? (
              <Button icon={Plus} onClick={() => setModal({ mode: 'create' })}>
                Nuevo feriado
              </Button>
            ) : null}
            <GuideLink moduleId="calendar" />
          </div>
        }
      />

      <Link to="/calendar" className="text-sm font-medium text-primary-600 hover:underline">
        Volver al calendario
      </Link>

      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="holiday-zone-filter" className="text-sm font-medium text-slate-900">
              Zona
            </label>
            <select
              id="holiday-zone-filter"
              value={search.zone ?? ''}
              onChange={(event) =>
                applyFilters({
                  zone:
                    event.target.value === ''
                      ? undefined
                      : (event.target.value as (typeof ZONES)[number]),
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todas las zonas</option>
              {ZONES.map((zone) => (
                <option key={zone} value={zone}>
                  {ZONE_LABELS[zone]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="holiday-year-filter" className="text-sm font-medium text-slate-900">
              Año
            </label>
            <input
              id="holiday-year-filter"
              type="number"
              value={search.year ?? ''}
              onChange={(event) =>
                applyFilters({
                  year: event.target.value === '' ? undefined : Number(event.target.value),
                })
              }
              className="h-11 w-28 rounded-md border border-slate-300 px-3 text-sm"
            />
          </div>
        </div>
      </Card>

      {actionError !== undefined ? <Alert tone="danger">{actionError}</Alert> : null}

      {holidaysQuery.isError ? (
        <Alert tone="danger" title="No pudimos cargar los feriados">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void holidaysQuery.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {holidaysQuery.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {holidaysQuery.data !== undefined && holidaysQuery.data.items.length === 0 ? (
        <EmptyState
          icon={CalendarOff}
          title="Todavía no hay feriados registrados."
          description="Elegí otro filtro o creá el primer feriado."
        />
      ) : null}

      {holidaysQuery.data !== undefined && holidaysQuery.data.items.length > 0 ? (
        <Table>
          <caption className="sr-only">Feriados</caption>
          <thead>
            <tr>
              <TableHeader>Zona</TableHeader>
              <TableHeader>Fecha</TableHeader>
              <TableHeader>Nombre</TableHeader>
              <TableHeader>Servicio</TableHeader>
              {canManage ? <TableHeader>Acciones</TableHeader> : null}
            </tr>
          </thead>
          <TableBody>
            {holidaysQuery.data.items.map((holiday) => (
              <TableRow key={holiday.id}>
                <TableCell>
                  {holiday.zone === null ? 'Todas las zonas' : ZONE_LABELS[holiday.zone]}
                </TableCell>
                <TableCell>{formatCalendarDate(holiday.date)}</TableCell>
                <TableCell className="font-medium text-slate-900">{holiday.name}</TableCell>
                <TableCell>
                  <StatusBadge
                    domain="holiday"
                    status={holiday.hasService ? 'HAS_SERVICE' : 'NO_SERVICE'}
                  />
                </TableCell>
                {canManage ? (
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Pencil}
                        onClick={() => setModal({ mode: 'edit', holiday })}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Trash2}
                        onClick={() => {
                          if (
                            window.confirm(
                              `¿Eliminar el feriado "${holiday.name}"? Cambia si hay servicio ese día.`,
                            )
                          ) {
                            deleteMutation.mutate(holiday.id);
                          }
                        }}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {modal !== null ? (
        <HolidayFormModal
          mode={modal.mode}
          defaultValues={modal.mode === 'edit' ? toHolidayFormValues(modal.holiday) : undefined}
          submitting={createMutation.isPending || updateMutation.isPending}
          formError={formError === '' ? undefined : formError}
          onCancel={() => setModal(null)}
          onSubmit={async (values) => {
            if (modal.mode === 'create') {
              await createMutation.mutateAsync(toHolidayCreateInput(values));
            } else {
              await updateMutation.mutateAsync({
                id: modal.holiday.id,
                input: toHolidayUpdateInput(values),
              });
            }
          }}
        />
      ) : null}
    </section>
  );
}
